import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {catchError, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {
  oneClickDeployModalAction,
  deployModalAction,
  loadEntityConfig,
  loadEntityConfigError, loadEntityConfigSuccess,
} from '../actions/entity-config.action';
import { ManagementService } from '../../services/management/management.service';
import { of } from 'rxjs';
import { SubmissionService } from '../../services/management/submission/submission.service';
import { toastBannerError, toastBannerSuccess } from '../actions/toastBanner.action';
import { SubmissionsApprovalRequestConfig } from '../../../shared/models/confirmModalConfig';
import { SubmissionType } from '../../../shared/models/flow-manager-core';
import {addNodeType, resetNodeType, setPermissions} from '../actions/flow.action';
import {aiAssistantNode, chat2payNodeObject, multiProductNode} from '../../../shared/utils/nodeTypes';
import {loadChatHubProviders} from '../../../core/state/actions/flow.action';
import {Store} from '@ngrx/store';
import {SharedStorageService} from "ngx-store";

@Injectable()
export class EntityConfigEffects {
  loadEntityConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEntityConfig),
      mergeMap(() =>
        this.managementService.getEntityConfigs().pipe(
          tap(() => this.store.dispatch(resetNodeType())),
          map((payload) => {
            this.addNodes(payload);
            this.setRoles();
            return loadEntityConfigSuccess({ payload });
          }),
          catchError(() => of(loadEntityConfigError))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private managementService: ManagementService,
    private store: Store,
    private sharedStorageService: SharedStorageService,
  ) { }

  addNodes(config: any) {
    if (config['chat2pay-node']) {
      this.store.dispatch(addNodeType({
        nodeObject: chat2payNodeObject,
        nodeName: "menuNodes"
      }));
    }
    if (config['chathub-node']) {
      this.store.dispatch(loadChatHubProviders());
    }
    if (config['multiProduct-node']) {
      this.store.dispatch(addNodeType({
        nodeObject: multiProductNode,
        nodeName: "menuNodes"
      }));
    }
    if (config['clickatell-ai']) {
      this.store.dispatch(addNodeType({
        nodeObject: aiAssistantNode,
        nodeName: "menuNodes"
      }));
    }
  }

  setRoles() {
    const permissions = this.sharedStorageService.get('userInfo')['permissions']
    this.store.dispatch(setPermissions({permissions: Object.fromEntries(permissions)}))
  }
}

const deployApproval = {
  success: {
    title: 'Flow Successfully Deployed',
    body: ' flow has been successfully deployed to production and will be live soon.'
  },
  error: {
    title: 'Failure to Deploy Flow',
    body: ' flow has not been successfully deployed to production due to an error that occurred.'
  }
};

const deleteApproval = {
  success: {
    title: 'Flow Successfully deleted',
    body: ' flow has been successfully deleted from production.'
  },
  error: {
    title: 'Failure to delete Flow',
    body: ' flow has not been successfully deleted due to an error that occurred.'
  }
};

const approvalsMessages = {
  'PROMOTE': deployApproval,
  'DELETE': deleteApproval
};

/* This class is responsible for handling the effects of the confirmation modal deploy action */
@Injectable()
export class DeployFlowEffects {
  loadDeployFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(oneClickDeployModalAction, deployModalAction),
      map(action => action.payload),
      switchMap(payload =>
        this.submissionService.deployToProd(payload.env, payload.submissionsApprovalRequest).pipe(
          map(() => {
            const deployMessages = this.getApprovalMessage(payload);
            return toastBannerSuccess({ title: deployMessages.success.title, body: payload.flowName + deployMessages.success.body });
          }),
          catchError((response) => {
            const deployMessages = this.getApprovalMessage(payload);
            return of(toastBannerError({ title: deployMessages.error.title, body: payload.flowName + deployMessages.error.body })
            );
          })
        )))
  );

  getApprovalMessage(payload: SubmissionsApprovalRequestConfig) {
    const submissionType: SubmissionType = payload.submissionsApprovalRequest.submissionType;
    return approvalsMessages[submissionType];
  }

  constructor(
    private actions$: Actions,
    private submissionService: SubmissionService
  ) { }
}


